import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiClient } from '../api/client';
import type { GameDetail, LeaderboardRow } from '../api/types';
import { config } from '../config';
import { useI18n } from '../i18n';

const api = new ApiClient({ baseUrl: config.apiBaseUrl });

type Window = '24h' | '7d' | 'all';

export function GamePage() {
  const { gameKey = '' } = useParams();
  const { t } = useI18n();
  const [game, setGame] = useState<GameDetail | null>(null);
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [window, setWindow] = useState<Window>('7d');

  useEffect(() => {
    if (!gameKey) return;
    const ac = new AbortController();
    api
      .getGame(gameKey, ac.signal)
      .then(setGame)
      .catch(() => undefined);
    return () => ac.abort();
  }, [gameKey]);

  useEffect(() => {
    if (!gameKey) return;
    const ac = new AbortController();
    setRows(null);
    api
      .getLeaderboard(gameKey, { window }, ac.signal)
      .then((r) => setRows(r.items))
      .catch(() => setRows([]));
    return () => ac.abort();
  }, [gameKey, window]);

  return (
    <div className="stack">
      <Link className="btn btn--ghost btn--sm" to="/" style={{ alignSelf: 'flex-start' }}>
        ← {t('game.back')}
      </Link>

      <h1 className="section-title">{game?.display_name ?? gameKey}</h1>

      {game ? (
        <section className="card stack stack--tight">
          <span className="faint">{t('games.variants')}</span>
          <ul className="variant-list">
            {game.variants
              .filter((v) => v.published)
              .map((v) => (
                <li className="variant-row" key={v.variant_key}>
                  <span>{v.display_name}</span>
                  <span className="mono">{v.variant_key}</span>
                </li>
              ))}
          </ul>
          <a className="btn btn--sm" href={game.lobby_url} style={{ alignSelf: 'flex-start' }}>
            {t('games.play')} →
          </a>
        </section>
      ) : null}

      <section className="card stack stack--tight" aria-labelledby="lb-title">
        <div className="spread">
          <h2 className="game-card__title" id="lb-title">
            {t('game.leaderboard')}
          </h2>
          <div className="row">
            {(['24h', '7d', 'all'] as Window[]).map((w) => (
              <button
                key={w}
                type="button"
                className={`btn btn--sm${window === w ? '' : ' btn--ghost'}`}
                aria-pressed={window === w}
                onClick={() => setWindow(w)}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        {rows === null ? (
          <p className="muted">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="muted">{t('game.leaderboard.empty')}</p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('game.rank')}</th>
                  <th scope="col">{t('game.player')}</th>
                  <th scope="col">{t('game.wins')}</th>
                  <th scope="col">{t('game.losses')}</th>
                  <th scope="col">{t('game.draws')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.rank}-${r.display_name}`}>
                    <td>{r.rank}</td>
                    {/* C02 §2：匿名玩家只以 display_name 呈現，不揭露 subject id。 */}
                    <td>{r.display_name}</td>
                    <td>{r.wins}</td>
                    <td>{r.losses}</td>
                    <td>{r.draws}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
